<template>
  <v-container fluid class="pa-0 vendedor-panel">
    <!-- Tabs -->
    <v-tabs v-model="tab" grow color="primary" class="vendedor-tabs">
      <v-tab>
        <v-icon left>mdi-barcode-scan</v-icon>
        Escanear
      </v-tab>
      <v-tab>
        <v-icon left>mdi-cart-outline</v-icon>
        Mis ventas
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- ── PESTAÑA ESCANEAR ───────────────────────────────── -->
      <v-tab-item>
        <div class="pa-3">
          <!-- Cámara -->
          <v-card outlined class="mb-3">
            <StreamBarcodeReader
              @decode="onScan"
              @loaded="onCameraLoaded"
              :stopCamera="stopCamera"
            />
            <v-divider />
            <v-card-actions>
              <v-btn small text @click="toggleCamera">
                <v-icon left small>{{ stopCamera ? "mdi-camera" : "mdi-camera-off" }}</v-icon>
                {{ stopCamera ? "Encender" : "Pausar" }}
              </v-btn>
              <v-spacer />
              <span v-if="existencesLoading" class="text-caption grey--text">
                <v-progress-circular indeterminate size="14" width="2" class="mr-1" />
                Cargando existencias...
              </span>
            </v-card-actions>
          </v-card>

          <!-- Ficha del producto -->
          <v-card v-if="ficha" outlined class="ficha-card">
            <v-card-title class="pb-1">
              <span class="text-subtitle-1 font-weight-bold">{{ ficha.nombre || ficha.refe || "Producto" }}</span>
              <v-spacer />
              <v-chip
                :color="existencias > 0 ? 'success' : 'error'"
                label
                class="font-weight-bold"
              >
                <v-icon left small>mdi-package-variant-closed</v-icon>
                {{ existenciasTexto }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-2">
              <div class="d-flex align-center mb-2">
                <span class="ficha-precio">{{ formatCurrency(ficha.precio) }}</span>
                <v-chip
                  v-if="ficha.descuento > 0"
                  x-small
                  color="error"
                  outlined
                  class="ml-2"
                >
                  -{{ formatCurrency(ficha.descuento) }}
                </v-chip>
              </div>

              <v-simple-table dense class="ficha-table">
                <tbody>
                  <tr v-if="ficha.marca || ficha.brand">
                    <td class="ficha-label">Marca</td>
                    <td>{{ ficha.brand || ficha.marca }}</td>
                  </tr>
                  <tr v-if="ficha.refe">
                    <td class="ficha-label">Referencia</td>
                    <td>{{ ficha.refe }}</td>
                  </tr>
                  <tr v-if="ficha.color || ficha.colorName">
                    <td class="ficha-label">Color</td>
                    <td>{{ ficha.colorName || ficha.color }}</td>
                  </tr>
                  <tr v-if="ficha.talla">
                    <td class="ficha-label">Talla</td>
                    <td>{{ ficha.talla }}</td>
                  </tr>
                  <tr v-if="ficha.category || ficha.categoria">
                    <td class="ficha-label">Categoría</td>
                    <td>{{ ficha.category || ficha.categoria }}</td>
                  </tr>
                  <tr>
                    <td class="ficha-label">EAN</td>
                    <td>{{ ficha.ean }}</td>
                  </tr>
                  <tr v-if="ficha.sku">
                    <td class="ficha-label">SKU</td>
                    <td>{{ ficha.sku }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- Estado vacío -->
          <div v-else class="text-center py-10 grey--text">
            <v-icon size="64" color="grey lighten-2">mdi-barcode-scan</v-icon>
            <h3 class="text-subtitle-1 mt-3 grey--text">Escanea un código de barras</h3>
            <p class="text-caption">Apunta la cámara al código del producto</p>
          </div>
        </div>
      </v-tab-item>

      <!-- ── PESTAÑA MIS VENTAS ─────────────────────────────── -->
      <v-tab-item>
        <div class="pa-3">
          <v-card outlined class="mb-3 sales-summary">
            <v-card-text class="d-flex align-center pa-3">
              <div>
                <div class="text-caption grey--text">Ventas de hoy</div>
                <div class="text-h6 font-weight-bold primary--text">{{ formatCurrency(totalVentas) }}</div>
              </div>
              <v-spacer />
              <div class="text-center mr-3">
                <div class="text-h6 font-weight-bold">{{ ventas.length }}</div>
                <div class="text-caption grey--text">docs</div>
              </div>
              <v-btn icon @click="loadVentas" :loading="ventasLoading" title="Actualizar">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>

          <v-card outlined>
            <v-list v-if="ventas.length" class="py-0">
              <template v-for="(doc, i) in ventas">
                <v-list-item :key="doc.id || doc.doc_number || i">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ doc.doc_type }} · {{ doc.doc_number }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ formatTime(doc.issuedate) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <span
                      class="font-weight-bold"
                      :class="doc.doc_type === 'NC' ? 'error--text' : 'primary--text'"
                    >
                      {{ doc.doc_type === "NC" ? "-" : "" }}{{ formatCurrency(doc.doc_value) }}
                    </span>
                  </v-list-item-action>
                </v-list-item>
                <v-divider :key="`${i}-divider`" />
              </template>
            </v-list>

            <div v-else-if="ventasLoading" class="text-center py-8 grey--text">
              <v-progress-circular indeterminate color="primary" />
              <div class="mt-2">Cargando ventas...</div>
            </div>

            <div v-else class="text-center py-10 grey--text">
              <v-icon size="64" color="grey lighten-2">mdi-cart-off</v-icon>
              <h3 class="text-subtitle-1 mt-3 grey--text">Sin ventas hoy</h3>
              <p class="text-caption">Aún no tienes ventas registradas hoy</p>
            </div>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import { StreamBarcodeReader } from "@/utils/barcodereader.js";

export default {
  name: "VendedorPanel",
  components: { StreamBarcodeReader },
  data() {
    return {
      tab: 0,
      // Escáner
      stopCamera: false,
      lastScan: { code: null, ts: 0 },
      ficha: null,
      // Existencias
      st_items: [],
      existencesLoading: false,
      // Ventas
      ventas: [],
      ventasLoading: false,
      snackbar: { show: false, text: "", color: "info", timeout: 3000 },
    };
  },
  computed: {
    // Cantidad disponible cruzada por EAN (o SKU) contra st_items
    existencias() {
      if (!this.ficha) return 0;
      return this.getExistencias(this.ficha);
    },
    existenciasTexto() {
      return `Existencias: ${this.existencias}`;
    },
    totalVentas() {
      return this.ventas.reduce((total, doc) => {
        const value = parseFloat(doc.doc_value || 0);
        return total + (doc.doc_type === "NC" ? -value : value);
      }, 0);
    },
  },
  mounted() {
    this.loadExistencias();
    this.loadVentas();
  },
  watch: {
    tab(val) {
      // Pausar cámara cuando no estamos en la pestaña de escaneo
      this.stopCamera = val !== 0;
      if (val === 1) this.loadVentas();
    },
  },
  methods: {
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = { show: true, text, color, timeout };
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "$ 0";
      return "$ " + parseFloat(value).toLocaleString("es-CO");
    },

    formatTime(dt) {
      if (!dt) return "";
      // dt típico: "YYYY-MM-DD HH:mm:ss"
      const parts = String(dt).split(" ");
      return parts.length > 1 ? parts[1].substring(0, 5) : dt;
    },

    // ── Cámara ────────────────────────────────────────────
    onCameraLoaded() {
      /* cámara lista */
    },

    toggleCamera() {
      this.stopCamera = !this.stopCamera;
    },

    onScan(code) {
      if (!code) return;
      const now = Date.now();
      // Evitar reescaneos repetidos del mismo código (debounce 2.5s)
      if (code === this.lastScan.code && now - this.lastScan.ts < 2500) return;
      this.lastScan = { code, ts: now };
      this.get_product(code);
    },

    // ── Ficha del producto ────────────────────────────────
    get_product(ean) {
      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: {
          id_pos: window.store.store_id,
          ean: ean,
        },
      };
      webserver("get_catalog", qry, (data) => {
        if (data && data.error) {
          this.showMessage("Error al consultar producto", "error");
          return;
        }
        if (Array.isArray(data) && data.length > 0) {
          this.ficha = data[0];
          this.showMessage("Producto encontrado", "success", 1500);
        } else {
          this.ficha = null;
          this.showMessage(`Producto no encontrado: ${ean}`, "warning");
        }
      });
    },

    // ── Existencias ───────────────────────────────────────
    loadExistencias() {
      this.existencesLoading = true;
      const qry = { store: window.store.store_id };
      webserver("get_inventory_st", qry, (data) => {
        this.existencesLoading = false;
        if (data && data.error) return;
        this.st_items = Array.isArray(data) ? data : [];
      });
    },

    // Cruza la ficha contra st_items por EAN (o SKU como respaldo)
    getExistencias(ficha) {
      if (!ficha || !this.st_items.length) return 0;
      const row =
        this.st_items.find((s) => s.ean && ficha.ean && s.ean === ficha.ean) ||
        this.st_items.find((s) => s.sku && ficha.sku && s.sku === ficha.sku);
      if (!row) return 0;
      // El campo de cantidad disponible puede variar según el backend.
      const candidates = [
        row.existencia,
        row.existencias,
        row.saldo,
        row.cantidad,
        row.stock,
      ];
      const qty = candidates.find((v) => v !== undefined && v !== null);
      return parseInt(qty || 0, 10);
    },

    // ── Ventas del día ────────────────────────────────────
    loadVentas() {
      this.ventasLoading = true;
      const qry = {
        store: window.store.store_id,
        table: "docs",
        filters: [
          {
            field: "doc_type",
            operator: " IN ",
            value: "'TK','FS','FC','FE','NC','ND'",
          },
          {
            field: "DATE(issuedate)",
            operator: "=",
            value: getToday(),
          },
          {
            field: "store",
            operator: "=",
            value: window.store.store_id,
          },
          {
            field: "vendor",
            operator: "=",
            value: window.profile.user_email,
          },
        ],
      };
      webserver("get_dataTable", qry, (data) => {
        this.ventasLoading = false;
        if (data && data.error) {
          this.showMessage("Error al cargar ventas", "error");
          return;
        }
        this.ventas = Array.isArray(data) ? data : [];
      });
    },
  },
};
</script>

<style scoped>
.vendedor-panel {
  max-width: 640px;
  margin: 0 auto;
}
.ficha-precio {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1976d2;
}
.ficha-table >>> td {
  height: 32px !important;
  font-size: 0.85rem;
}
.ficha-label {
  color: #888;
  width: 100px;
  font-size: 0.78rem !important;
}
.sales-summary {
  background: #f7faff;
}
</style>
